import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ArrowLeft,
  CalendarClock,
  Clock3,
  GripVertical,
  LayoutGrid,
  List,
  Loader2,
  Pencil,
  Plus,
  Save,
  Table2,
  Trash2,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { isAdminRole } from "../../lib/roles";
import { Card, EmptyState, PageHeader, formatDate } from "../ui";

const basePath = (user) =>
  isAdminRole(user?.role) ? "/admin/comercial" : "/portal/comercial";

const daysBetween = (iso) => {
  if (!iso) return 0;
  try {
    const d = new Date(iso);
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  } catch {
    return 0;
  }
};

// ===========================================================================
// KANBAN — Funil Comercial
// ===========================================================================

export const ComercialKanban = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminRole(user?.role);
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/comercial/stages"),
        api.get("/comercial/leads", { params: { page: 1, limit: 200 } }),
      ]);
      setStages(s.data.items || []);
      setLeads(l.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = {};
    stages.forEach((s) => (map[s.key] = []));
    leads.forEach((l) => {
      const key = l.stage || "novo_lead";
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });
    return map;
  }, [stages, leads]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;
    if (!isAdmin) {
      toast.error("Apenas administradores podem mover cards");
      return;
    }
    const newStage = destination.droppableId;
    // Optimistic update
    const prev = leads;
    setLeads((cur) =>
      cur.map((l) =>
        l.id === draggableId
          ? { ...l, stage: newStage, stage_updated_at: new Date().toISOString() }
          : l
      )
    );
    setSaving(true);
    try {
      await api.post(`/comercial/leads/${draggableId}/stage`, {
        stage: newStage,
      });
    } catch (err) {
      setLeads(prev); // rollback
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 className="inline animate-spin mr-2" size={16} /> Carregando funil...
      </div>
    );
  }

  return (
    <div data-testid="comercial-kanban" className="p-6 lg:p-10">
      <PageHeader
        title="Funil Comercial"
        subtitle={
          isAdmin
            ? "Arraste os cards entre as colunas para alterar o estágio dos leads."
            : "Visualização do funil comercial. A movimentação é restrita à administração."
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {saving && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> salvando...
              </span>
            )}
            <div className="inline-flex rounded border border-slate-300 bg-white overflow-hidden">
              <Link
                to={`${basePath(user)}/dashboard`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Dashboard
              </Link>
              <Link
                to={`${basePath(user)}/leads`}
                data-testid="btn-view-table"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Table2 size={14} /> Tabela
              </Link>
              <span className="px-3 py-2 text-sm bg-[#002776] text-white inline-flex items-center gap-2">
                <LayoutGrid size={14} /> Kanban
              </span>
            </div>
            {isAdmin && (
              <>
                <Link
                  to={`${basePath(user)}/estagios`}
                  className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm rounded hover:bg-slate-50"
                  data-testid="btn-manage-stages"
                >
                  <Pencil size={14} /> Estágios
                </Link>
                <Link
                  to={`${basePath(user)}/leads/novo`}
                  className="inline-flex items-center gap-2 bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110"
                  data-testid="btn-new-lead-kanban"
                >
                  <Plus size={16} /> Indicar associado
                </Link>
              </>
            )}
          </div>
        }
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-6 lg:-mx-10 px-6 lg:px-10">
          {stages.map((stage) => {
            const items = grouped[stage.key] || [];
            return (
              <Droppable
                droppableId={stage.key}
                key={stage.key}
                isDropDisabled={!isAdmin}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    data-testid={`kanban-col-${stage.key}`}
                    className={`shrink-0 w-72 bg-slate-100/70 rounded-md border ${
                      snapshot.isDraggingOver
                        ? "border-[#002776] bg-blue-50/40"
                        : "border-slate-200"
                    }`}
                  >
                    <div
                      className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200 rounded-t-md"
                      style={{
                        background: `linear-gradient(0deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9)), ${stage.color}`,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: stage.color }}
                        />
                        <h3 className="text-sm font-semibold text-slate-800">
                          {stage.label}
                        </h3>
                      </div>
                      <span className="text-[11px] text-slate-600 font-medium bg-white border border-slate-200 rounded-full px-2 py-0.5">
                        {items.length}
                      </span>
                    </div>

                    <div className="p-2 min-h-[200px] space-y-2">
                      {items.map((lead, idx) => (
                        <Draggable
                          draggableId={lead.id}
                          index={idx}
                          key={lead.id}
                          isDragDisabled={!isAdmin}
                        >
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              onClick={() =>
                                navigate(
                                  `${basePath(user)}/leads/${lead.id}`
                                )
                              }
                              data-testid={`kanban-card-${lead.id}`}
                              className={`bg-white border rounded-md p-3 shadow-sm cursor-pointer hover:border-[#002776] transition ${
                                snap.isDragging
                                  ? "border-[#002776] shadow-md"
                                  : "border-slate-200"
                              }`}
                            >
                              <div className="font-semibold text-sm text-slate-900 line-clamp-2">
                                {lead.razao_social}
                              </div>
                              {lead.segmento && (
                                <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">
                                  {lead.segmento}
                                </div>
                              )}
                              <div className="mt-2 text-xs text-slate-600 space-y-1">
                                <div className="flex items-center gap-1">
                                  <Pencil size={11} />
                                  <span>
                                    {lead.indicated_by_name || "—"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <CalendarClock size={11} />
                                  <span>
                                    Entrou em{" "}
                                    {formatDate(lead.created_at)}
                                  </span>
                                </div>
                                {lead.assigned_to_name && (
                                  <div className="text-[11px] text-slate-700">
                                    Resp.{" "}
                                    <strong>{lead.assigned_to_name}</strong>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                  <Clock3 size={11} />
                                  {daysBetween(
                                    lead.stage_updated_at || lead.created_at
                                  )}
                                  d neste estágio
                                </span>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {items.length === 0 && (
                        <div className="text-[11px] text-slate-400 italic text-center py-6">
                          vazio
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
};

// ===========================================================================
// GESTÃO DE ESTÁGIOS (admin)
// ===========================================================================

const PRESET_COLORS = [
  "#64748b", "#6366f1", "#0ea5e9", "#06b6d4", "#f59e0b",
  "#f97316", "#eab308", "#22c55e", "#16a34a", "#ef4444",
  "#8b5cf6", "#ec4899", "#14b8a6", "#84cc16",
];

export const ComercialEstagios = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#64748b");
  const [editing, setEditing] = useState({}); // {id: {label, color}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comercial/stages");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await api.post("/comercial/stages", {
        label: newLabel.trim(),
        color: newColor,
      });
      setNewLabel("");
      await load();
      toast.success("Estágio criado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s) =>
    setEditing((e) => ({ ...e, [s.id]: { label: s.label, color: s.color } }));

  const cancelEdit = (id) =>
    setEditing((e) => {
      const { [id]: _, ...rest } = e;
      return rest;
    });

  const saveEdit = async (s) => {
    const draft = editing[s.id];
    if (!draft) return;
    try {
      await api.patch(`/comercial/stages/${s.id}`, draft);
      cancelEdit(s.id);
      await load();
      toast.success("Estágio atualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const remove = async (s) => {
    if (
      !window.confirm(
        `Excluir estágio "${s.label}"? Esta ação só é permitida se não houver leads nele.`
      )
    )
      return;
    try {
      await api.delete(`/comercial/stages/${s.id}`);
      await load();
      toast.success("Estágio removido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);
    try {
      await api.post("/comercial/stages/reorder", {
        order: arr.map((a) => a.key),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      load();
    }
  };

  return (
    <div data-testid="comercial-estagios" className="p-6 lg:p-10 max-w-4xl">
      <PageHeader
        title="Estágios do funil comercial"
        subtitle="Nome, cor e ordem são configuráveis. Estágios iniciais e terminais são protegidos para preservar o histórico."
        actions={
          <Link
            to={`${basePath(user)}/kanban`}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Voltar ao Kanban
          </Link>
        }
      />

      <Card className="p-5 mb-4">
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
              Novo estágio
            </label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Ex: Follow-up após 60 dias"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="input-new-stage"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
              Cor
            </label>
            <div className="flex flex-wrap gap-1">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={`h-6 w-6 rounded-full border-2 ${
                    newColor === c ? "border-slate-900" : "border-white"
                  }`}
                  style={{ background: c }}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={saving || !newLabel.trim()}
            data-testid="btn-create-stage"
            className="bg-[#002776] text-white px-4 py-2 text-sm rounded font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Adicionar
          </button>
        </form>
      </Card>

      {loading ? (
        <div className="text-center py-6 text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Nenhum estágio" />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="stages">
            {(prov) => (
              <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-2">
                {items.map((s, idx) => {
                  const draft = editing[s.id];
                  return (
                    <Draggable draggableId={s.id} index={idx} key={s.id}>
                      {(dprov, snap) => (
                        <div
                          ref={dprov.innerRef}
                          {...dprov.draggableProps}
                          data-testid={`stage-row-${s.key}`}
                          className={`bg-white border rounded-md p-3 flex items-center gap-3 ${
                            snap.isDragging ? "border-[#002776] shadow-md" : "border-slate-200"
                          }`}
                        >
                          <span
                            {...dprov.dragHandleProps}
                            className="text-slate-400 hover:text-slate-700 cursor-grab"
                          >
                            <GripVertical size={16} />
                          </span>
                          {draft ? (
                            <>
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
                                <input
                                  value={draft.label}
                                  onChange={(e) =>
                                    setEditing((v) => ({
                                      ...v,
                                      [s.id]: { ...v[s.id], label: e.target.value },
                                    }))
                                  }
                                  className="md:col-span-3 border border-slate-300 rounded px-2 py-1 text-sm"
                                  data-testid={`edit-label-${s.key}`}
                                />
                                <div className="md:col-span-2 flex flex-wrap gap-1">
                                  {PRESET_COLORS.map((c) => (
                                    <button
                                      key={c}
                                      type="button"
                                      onClick={() =>
                                        setEditing((v) => ({
                                          ...v,
                                          [s.id]: { ...v[s.id], color: c },
                                        }))
                                      }
                                      className={`h-5 w-5 rounded-full border-2 ${
                                        draft.color === c ? "border-slate-900" : "border-white"
                                      }`}
                                      style={{ background: c }}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => saveEdit(s)}
                                  data-testid={`btn-save-${s.key}`}
                                  className="p-1.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                >
                                  <Save size={14} />
                                </button>
                                <button
                                  onClick={() => cancelEdit(s.id)}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <span
                                className="h-3 w-3 rounded-full"
                                style={{ background: s.color }}
                              />
                              <div className="flex-1">
                                <div className="font-medium text-slate-900 text-sm">
                                  {s.label}
                                  {s.protected && (
                                    <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                      protegido
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500">
                                  key: <code>{s.key}</code> · ordem #{s.order}
                                </div>
                              </div>
                              <button
                                onClick={() => startEdit(s)}
                                data-testid={`btn-edit-${s.key}`}
                                className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                              >
                                <Pencil size={14} />
                              </button>
                              {!s.protected && (
                                <button
                                  onClick={() => remove(s)}
                                  data-testid={`btn-delete-${s.key}`}
                                  className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};
